import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import CloseIcon from '@mui/icons-material/Close';
import Modal from '../Modal';
import { useAllCohorts } from '../../hooks/useStudentRankings';
import {
  formsError,
  useAvailableForms,
  useCreateAssignments,
} from '../../hooks/useFormAssignments';
import {
  FormStatusBadge,
  INPUT,
  PRIMARY,
  SECONDARY,
  dueAtFromDay,
} from './formsText';

function FormPicker({ value, onChange }) {
  const [input, setInput] = useState('');
  const [q, setQ] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setQ(input.trim()), 300);
    return () => clearTimeout(timer);
  }, [input]);
  const { data: forms = [], isFetching, error } = useAvailableForms(q, !value);

  if (value) {
    return (
      <div className="flex items-center justify-between rounded-md border border-gray-300 px-3 py-2 text-sm">
        <span>
          {value.title} <FormStatusBadge status={value.status} />
        </span>
        <button
          type="button"
          onClick={() => onChange(null)}
          className="text-xs text-claret-500 hover:underline"
        >
          Change
        </button>
      </div>
    );
  }
  return (
    <div className="space-y-1">
      <input
        type="search"
        aria-label="Search forms"
        placeholder="Search open forms"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        className={`${INPUT} w-full`}
      />
      <ul className="m-0 max-h-48 list-none overflow-y-auto rounded-md border border-gray-200 p-0">
        {isFetching && (
          <li className="px-3 py-2 text-xs text-gray-500">Loading forms…</li>
        )}
        {error && (
          <li className="px-3 py-2 text-xs text-red-700">
            {formsError(error, "We couldn't load the forms.")}
          </li>
        )}
        {!isFetching && !error && forms.length === 0 && (
          <li className="px-3 py-2 text-xs text-gray-500">
            No forms match. Only open forms that identify people by email,
            without file uploads, can be assigned.
          </li>
        )}
        {forms.map((form) => (
          <li key={form.slug}>
            <button
              type="button"
              onClick={() => onChange(form)}
              className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left hover:bg-gray-50"
            >
              <span className="min-w-0">
                <span className="block truncate text-sm text-gray-900">
                  {form.title}
                </span>
                <span className="block text-[11px] text-gray-500">
                  {[
                    form.purpose,
                    form.questions && `${form.questions} questions`,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </span>
              <FormStatusBadge status={form.status} />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function CohortPicker({ value, onChange, conflicts = [] }) {
  const { data, isLoading } = useAllCohorts();
  const cohorts = data?.data?.cohorts ?? [];
  const chosen = new Set(value.map((c) => c.id));
  return (
    <div className="space-y-2">
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {value.map((c) => (
            <span
              key={c.id}
              className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs ${
                conflicts.includes(c.id)
                  ? 'bg-red-50 text-red-700 ring-1 ring-red-200'
                  : 'bg-gray-100 text-gray-800'
              }`}
            >
              {c.name}
              <button
                type="button"
                aria-label={`Remove ${c.name}`}
                onClick={() => onChange(value.filter((x) => x.id !== c.id))}
                className="text-gray-400 hover:text-gray-700"
              >
                <CloseIcon sx={{ fontSize: 12 }} />
              </button>
            </span>
          ))}
        </div>
      )}
      <select
        aria-label="Add a cohort"
        value=""
        disabled={isLoading}
        onChange={(e) => {
          const cohort = cohorts.find((c) => c.id === e.target.value);
          if (cohort)
            onChange([...value, { id: cohort.id, name: cohort.name }]);
        }}
        className={`${INPUT} w-full`}
      >
        <option value="">
          {isLoading ? 'Loading cohorts…' : '+ Add a cohort…'}
        </option>
        {cohorts
          .filter((c) => !chosen.has(c.id))
          .map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
              {c.status ? ` (${c.status})` : ''}
            </option>
          ))}
      </select>
    </div>
  );
}

export default function AssignDialog({ open, onClose, defaultCohort }) {
  const create = useCreateAssignments();
  const [form, setForm] = useState(null);
  const [cohorts, setCohorts] = useState(defaultCohort ? [defaultCohort] : []);
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [dueDay, setDueDay] = useState('');
  const [notify, setNotify] = useState(true);
  const [error, setError] = useState(null);
  const [problems, setProblems] = useState([]);
  // Cohorts that already have this form (409 ALREADY_ASSIGNED).
  const [conflicts, setConflicts] = useState([]);

  const reset = () => {
    setForm(null);
    setCohorts(defaultCohort ? [defaultCohort] : []);
    setTitle('');
    setMessage('');
    setDueDay('');
    setNotify(true);
    setError(null);
    setProblems([]);
    setConflicts([]);
  };
  const close = () => {
    reset();
    onClose();
  };
  const today = new Date(Date.now() + 3 * 60 * 60 * 1000)
    .toISOString()
    .slice(0, 10);

  const handleError = (err) => {
    const details = err?.response?.data?.details;
    setError(formsError(err, "We couldn't assign the form."));
    setProblems(details?.problems ?? []);
    setConflicts(details?.cohort_ids ?? []);
  };

  const submit = () =>
    create.mutate(
      {
        form_slug: form.slug,
        cohort_ids: cohorts.map((c) => c.id),
        title: title.trim(),
        message: message.trim(),
        due_at: dueAtFromDay(dueDay),
        notify,
      },
      {
        onSuccess: (data) => {
          const n = data?.assignments?.length ?? cohorts.length;
          toast.success(`Assigned to ${n} ${n === 1 ? 'cohort' : 'cohorts'}.`);
          close();
        },
        onError: (err) => handleError(err),
      }
    );

  return (
    <Modal modalOpen={open} onClose={close} title="Assign a form">
      <div className="space-y-4 text-left">
        <div className="space-y-1">
          <p className="text-xs font-medium text-gray-700">Form</p>
          <FormPicker value={form} onChange={setForm} />
        </div>
        <div className="space-y-1">
          <p className="text-xs font-medium text-gray-700">Cohorts</p>
          <CohortPicker
            value={cohorts}
            onChange={(next) => {
              setCohorts(next);
              setConflicts((ids) =>
                ids.filter((id) => next.some((c) => c.id === id))
              );
            }}
            conflicts={conflicts}
          />
        </div>
        <label className="flex flex-col gap-1 text-xs font-medium text-gray-700">
          Title (optional)
          <input
            type="text"
            value={title}
            placeholder={form?.title || 'Defaults to the form’s title'}
            onChange={(e) => setTitle(e.target.value)}
            className={INPUT}
          />
        </label>
        <label className="flex flex-col gap-1 text-xs font-medium text-gray-700">
          Message to students (optional)
          <textarea
            rows={2}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            className={INPUT}
          />
        </label>
        <label className="flex flex-col gap-1 text-xs font-medium text-gray-700">
          Due date (optional, end of day in Kenya time)
          <input
            type="date"
            min={today}
            value={dueDay}
            onChange={(e) => setDueDay(e.target.value)}
            className={`${INPUT} w-48`}
          />
        </label>
        <label className="flex items-start gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            className="mt-1"
            checked={notify}
            onChange={(e) => setNotify(e.target.checked)}
          />
          <span>
            Tell the cohorts&apos; students now
            <span className="block text-xs text-gray-500">
              An in-app notification and an email: “New form:{' '}
              {title.trim() || form?.title || '…'}”.
            </span>
          </span>
        </label>
        {error && (
          <div className="space-y-1 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            <p>{error}</p>
            {problems.map((p) => (
              <p key={`${p.field}-${p.message}`} className="text-xs">
                {p.message}
              </p>
            ))}
            {conflicts.length > 0 && (
              <button
                type="button"
                onClick={() => {
                  setCohorts((list) =>
                    list.filter((c) => !conflicts.includes(c.id))
                  );
                  setConflicts([]);
                  setError(null);
                }}
                className="text-xs font-medium text-red-800 underline"
              >
                Remove{' '}
                {conflicts.length === 1
                  ? 'that cohort'
                  : `those ${conflicts.length} cohorts`}{' '}
                and try again
              </button>
            )}
          </div>
        )}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={close} className={SECONDARY}>
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!form || !cohorts.length || create.isLoading}
            className={PRIMARY}
          >
            {create.isLoading
              ? 'Assigning…'
              : `Assign${
                  cohorts.length > 1 ? ` to ${cohorts.length} cohorts` : ''
                }`}
          </button>
        </div>
      </div>
    </Modal>
  );
}
